import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { useFormField } from 'cng-common/features/form'
import { selectQbrCurrentFQuarter } from 'partner360/features/QBR/selectors/selectQbrCurrentFQuarter'
import { selectQbrPeriodOptions } from 'partner360/features/QBR/selectors/selectQbrPeriodOptions'
import { selectQbrSelectedPeriod } from 'partner360/features/QBR/selectors/selectQbrSelectedPeriod'

import { QBR_SETTINGS_FORM_KEY } from '../../../configs'
import { IToggleItemState } from '../components/toggleItem'
import { DEFAULT_SELECTED_QUARTERS } from '../configs'

export const usePeriod = () => {
  const [showFQuarterWarning, setShowFQuarterWarning] = useState(false)

  const qbrCurrentFQuarter = useSelector(selectQbrCurrentFQuarter)
  const options = useSelector(selectQbrPeriodOptions)
  const initialSelectedPeriod = useSelector(selectQbrSelectedPeriod)

  const fYearsOptions = useMemo(() => {
    return options.map((it) => it.fYear)
  }, [options])

  const { value: selectedPeriodFYear, handleChange: onChangeSelectedPeriodFYear } =
    useFormField<number>({
      defaultValue: initialSelectedPeriod.fYear,
      field: 'selectedPeriodFYear',
      formKey: QBR_SETTINGS_FORM_KEY,
    })

  const { value: selectedPeriodFQuarters, handleChange: onChangeSelectedPeriodFQuarters } =
    useFormField<number[]>({
      defaultValue: initialSelectedPeriod.fQuarters.map((it) => it.fQuarter),
      field: 'selectedPeriodFQuarters',
      formKey: QBR_SETTINGS_FORM_KEY,
    })

  const fQuartersOptions = useMemo(() => {
    const selectedPeriodOptions = options.find((it) => it.fYear === selectedPeriodFYear)
    if (!selectedPeriodOptions) {
      return []
    }

    return selectedPeriodOptions.fQuarters
  }, [options, selectedPeriodFYear])

  const handleChangeFQuarters = useCallback(
    (newQuarterState: IToggleItemState) => {
      const isCurrentFQuarterSelected =
        newQuarterState.isActive && newQuarterState.value === qbrCurrentFQuarter
      if (isCurrentFQuarterSelected) {
        setShowFQuarterWarning(true)
      }

      const newQuarterStateIndex = selectedPeriodFQuarters.findIndex(
        (fQuarter) => fQuarter === newQuarterState.value
      )

      if (newQuarterStateIndex !== -1) {
        const newQuarters = selectedPeriodFQuarters.filter(
          (_, index) => index <= newQuarterStateIndex
        )

        return onChangeSelectedPeriodFQuarters(newQuarters)
      } else {
        const selectedQuarters = fQuartersOptions
          .filter((it) => it.fQuarter <= newQuarterState.value)
          .map((it) => it.fQuarter)

        return onChangeSelectedPeriodFQuarters(selectedQuarters)
      }
    },
    [fQuartersOptions, selectedPeriodFQuarters, onChangeSelectedPeriodFQuarters, qbrCurrentFQuarter]
  )

  const handleChangeFYear = useCallback(
    (newYearState: IToggleItemState) => {
      if (initialSelectedPeriod.fYear === newYearState.value) {
        onChangeSelectedPeriodFQuarters(initialSelectedPeriod.fQuarters.map((it) => it.fQuarter))
      } else {
        onChangeSelectedPeriodFQuarters(DEFAULT_SELECTED_QUARTERS)
      }

      return onChangeSelectedPeriodFYear(newYearState.value)
    },
    [
      initialSelectedPeriod.fQuarters,
      initialSelectedPeriod.fYear,
      onChangeSelectedPeriodFQuarters,
      onChangeSelectedPeriodFYear,
    ]
  )

  const closeFQuarterWarningDialog = useCallback(() => {
    setShowFQuarterWarning(false)
  }, [])

  return {
    fYearsOptions,
    fQuartersOptions,
    selectedPeriodFYear,
    selectedPeriodFQuarters,
    handleChangeFQuarters,
    handleChangeFYear,
    showFQuarterWarning,
    closeFQuarterWarningDialog,
  }
}
